/**
 * Settings → System tab.
 *
 * The team-scoped equivalent of lemon_agent's Providers + Health page. We
 * deliberately drop lemon_agent's "Danger zone" (reset config / uninstall
 * service) — knode-os is multi-tenant SaaS, so there is no per-tenant "wipe
 * the install" affordance.
 *
 * Two cards:
 *
 * 1. Service Health — pings api-gateway's `/healthz` so the user can tell at
 *    a glance whether the backend is reachable.
 * 2. LLM models — same `LLMConfigsCard` rendered on `/llm`. Kept here as a
 *    convenience for admins already inside settings; the canonical full
 *    page lives at `/llm`.
 */

import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LLMConfigsCard } from "@/features/llm/configs-card";
import { apiRaw } from "@/lib/api";

interface SystemTabProps {
  isAdmin: boolean;
}

export function SystemTab({ isAdmin }: SystemTabProps) {
  return (
    <div className="space-y-4">
      <ServiceHealthCard />
      <LLMConfigsCard isAdmin={isAdmin} />
    </div>
  );
}

type HealthState = "loading" | "up" | "down";

function ServiceHealthCard() {
  const [state, setState] = useState<HealthState>("loading");
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function ping() {
      // `/v1/health` is the gateway's unauthenticated mirror of `/healthz`.
      // We deliberately don't reuse `/me` because a 401 (e.g. expired
      // session) would falsely report the gateway as down.
      try {
        const resp = await apiRaw("/health", { method: "GET" });
        if (cancelled) return;
        setState(resp.ok ? "up" : "down");
      } catch {
        if (cancelled) return;
        setState("down");
      } finally {
        if (!cancelled) setCheckedAt(new Date());
      }
    }

    void ping();
    const id = setInterval(() => void ping(), 15_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service health</CardTitle>
        <CardDescription>
          Live status of the api-gateway → database round-trip. The page re-checks every 15 seconds.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3">
        {state === "loading" ? (
          <Badge variant="outline">checking…</Badge>
        ) : state === "up" ? (
          <Badge variant="ok">up</Badge>
        ) : (
          <Badge variant="err">down</Badge>
        )}
        <span className="text-xs text-muted-foreground">
          {checkedAt
            ? `Last checked ${checkedAt.toLocaleTimeString()}`
            : "Probing for the first time…"}
        </span>
      </CardContent>
    </Card>
  );
}
